import {
  type ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';

import { routes } from './app.routes';

import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { environment } from '@env';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import {
  tokenInterceptor,
  userRefreshhInterceptor,
} from '@tvd/data-access/auth';
import { cacheInterceptor } from '@tvd/data-access/cache';
import { offlineInterceptor } from '@tvd/data-access/connectivity';
import {
  GlobalErrorHandler,
  errorInterceptor,
  safeViewTransitionHandler,
} from '@tvd/data-access/error-handling';
import { provideEventBusDevLogger } from '@tvd/cdk/events';
import { provideFeatureFlags } from '@tvd/data-access/posthog';
import { progressInterceptor } from '@tvd/data-access/progress';
import { provideCachedTranslateLoader } from '@tvd/data-access/translate-loader';
import { UpdatesModule } from '@tvd/data-access/updates';
import { provideFormErrors } from '@tvd/design-system/form-error';
import { provideIcons } from '@tvd/design-system/icon';
import { ACTIVE_LOCALE } from '@tvd/cdk/locale';
import { injectActiveLocale } from '@tvd/ui/locale';
import { TRIAL_BANNER_CONFIG } from '@tvd/ui/trial-banner/trial-banner-config';
import { commonErrorMessages } from '@tvd/utils/form';
import { init } from './init';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withViewTransitions({
        onViewTransitionCreated: safeViewTransitionHandler,
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideHttpClient(withXhr(), 
      withInterceptors([
        tokenInterceptor,
        userRefreshhInterceptor,
        progressInterceptor,
        offlineInterceptor,
        cacheInterceptor,
        errorInterceptor,
      ]),
    ),
    provideAnimationsAsync(),
    provideHotToastConfig({
      position: 'top-center',
      style: {
        marginTop: '3rem',
      },
      duration: 4000,
      dismissible: true,
      role: 'status',
      ariaLive: 'polite',
      stacking: 'depth',
    }),
    provideAppInitializer(init),
    provideTranslateService({
      loader: provideCachedTranslateLoader(),
      fallbackLang: 'de',
    }),
    importProvidersFrom(UpdatesModule),
    { provide: ACTIVE_LOCALE, useFactory: () => injectActiveLocale() },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    provideFormErrors({
      errors: {
        useFactory: commonErrorMessages,
        deps: [TranslateService],
      },
    }),
    provideFeatureFlags(),
    {
      provide: TRIAL_BANNER_CONFIG,
      useValue: { showTrialBanner: environment.showTrialBanner },
    },
    provideSignalFormsConfig({
      classes: {
        'ng-valid': ({ state }) => state().valid(),
        'ng-invalid': ({ state }) => state().invalid(),
        'ng-touched': ({ state }) => state().touched(),
        'ng-dirty': ({ state }) => state().dirty(),
      },
    }),
    provideIcons({ mode: environment.production ? 'svg' : 'font' }),
    // Dev-only observability tap (issue 0004): logs every bus event in dev;
    // a no-op in production, guarded by isDevMode().
    provideEventBusDevLogger(),
  ],
};
